import React, { useRef, useState, useEffect } from 'react';
import { X, Share2, Download, Facebook, Twitter, Link2, Check, Instagram, MapPin } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useApp } from '../context/AppContext';
import { nativeShare, canShare, CANONICAL_ORIGIN } from '../lib/shareUtils';
import { shareImageToInstagramStories } from '../lib/instagramShare';
import { computeOverallRating } from '../utils/ratings';

function ShareReviewModal({ isOpen, onClose, bar, rating, user, profile }) {
  const cardRef = useRef(null);
  const modalRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const { trackShare } = useApp();

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const overallScore = Number(rating?.overall) > 0
    ? Number(rating.overall)
    : computeOverallRating(rating || {});
  const shareUrl = rating?.id
    ? `${CANONICAL_ORIGIN}/share/${rating.id}`
    : `${CANONICAL_ORIGIN}/bar/${bar.id}`;
  const shareText = `I just rated ${bar.name} ${overallScore}/5 stars on Tilted! 🍺`;

  // Helper to draw a 5-point star (points UP)
  function drawStar(ctx, cx, cy, spikes, outerRadius, innerRadius) {
    let angle = -Math.PI / 2; // Start at top (12 o'clock)
    const step = Math.PI / spikes;

    ctx.beginPath();
    for (let i = 0; i < spikes * 2; i++) {
      const r = i % 2 === 0 ? outerRadius : innerRadius;
      const x = cx + Math.cos(angle) * r;
      const y = cy + Math.sin(angle) * r;
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
      angle += step;
    }
    ctx.closePath();
    ctx.fill();
  }

  // Load an image and return a promise
  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  // Generate the review card canvas and filename
  const generateCardCanvas = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Instagram Story dimensions — full bleed, no inner card
    const W = 1080;
    const H = 1920;
    canvas.width = W;
    canvas.height = H;

    // ---- FULL-BLEED BACKGROUND (same gradient as SMS card) ----
    const bgGrad = ctx.createLinearGradient(0, 0, W, H);
    bgGrad.addColorStop(0, '#0e0e12');
    bgGrad.addColorStop(1, '#141418');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, W, H);

    // Subtle neon border around entire canvas edge
    ctx.strokeStyle = 'rgba(255, 45, 85, 0.25)';
    ctx.lineWidth = 4;
    ctx.strokeRect(0, 0, W, H);

    ctx.textAlign = 'center';
    const cx = W / 2;

    // ---- Calculate layout: distribute content across full height ----
    const hasSubRatings = [rating.price, rating.ambiance, rating.crowd, rating.service, rating.drinkSelection].some(v => v != null && v > 0);
    const hasDiveBadge = rating.isDiveBar;

    // Fixed content heights
    const logoH = 240;
    const barNameH = 80;
    const neighborhoodH = 45;
    const starsH = 100;
    const ratingNumH = 100;
    const diveBadgeH = hasDiveBadge ? 60 : 0;
    const subRatingsH = hasSubRatings ? 150 : 0;
    const dividerReviewerH = 80;
    const footerH = 180;

    const totalContentH = logoH + barNameH + neighborhoodH + starsH + ratingNumH + diveBadgeH + subRatingsH + dividerReviewerH + footerH;
    const availableH = H - 120; // 60px top + 60px bottom padding
    const extraSpace = availableH - totalContentH;

    // Distribute extra space as gaps between sections
    const sectionCount = 8 + (hasDiveBadge ? 1 : 0) + (hasSubRatings ? 1 : 0);
    const gap = Math.floor(extraSpace / sectionCount);

    let y = 60; // top padding

    // ---- LOGO ----
    try {
      const logoImg = await loadImage('/logo-full.png');
      const logoDrawH = 240;
      const logoDrawW = (logoImg.width / logoImg.height) * logoDrawH;
      ctx.drawImage(logoImg, (W - logoDrawW) / 2, y, logoDrawW, logoDrawH);
      y += logoDrawH + gap;
    } catch (e) {
      ctx.fillStyle = '#ff2d55';
      ctx.font = 'bold 110px system-ui';
      ctx.fillText('TILTED', cx, y + 100);
      y += 160 + gap;
    }

    // ---- BAR NAME ----
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 84px system-ui';
    const barName = bar.name.length > 18 ? bar.name.substring(0, 18) + '...' : bar.name;
    ctx.fillText(barName, cx, y + 70);
    y += barNameH + gap * 0.3;

    // Neighborhood
    ctx.fillStyle = '#888';
    ctx.font = '42px system-ui';
    ctx.fillText(`📍 ${bar.neighborhood || ''}`, cx, y + 40);
    y += neighborhoodH + gap;

    // ---- RATING STARS ----
    const starSize = 100;
    const starGap = 28;
    const totalStarsWidth = 5 * starSize + 4 * starGap;
    const starsStartX = (W - totalStarsWidth) / 2;

    for (let i = 0; i < 5; i++) {
      const starCx = starsStartX + i * (starSize + starGap) + starSize / 2;
      const starNum = i + 1;
      const filled = overallScore >= starNum;
      const half = overallScore >= starNum - 0.5 && overallScore < starNum;

      ctx.fillStyle = '#3a3a42';
      drawStar(ctx, starCx, y + starSize / 2, 5, starSize / 2, starSize / 5);

      if (filled) {
        ctx.fillStyle = '#ffb700';
        drawStar(ctx, starCx, y + starSize / 2, 5, starSize / 2, starSize / 5);
      } else if (half) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(starCx - starSize / 2, y, starSize / 2, starSize);
        ctx.clip();
        ctx.fillStyle = '#ffb700';
        drawStar(ctx, starCx, y + starSize / 2, 5, starSize / 2, starSize / 5);
        ctx.restore();
      }
    }
    y += starsH + gap * 0.5;

    // ---- RATING NUMBER ----
    ctx.fillStyle = '#ffb700';
    ctx.font = 'bold 110px system-ui';
    ctx.fillText(`${overallScore.toFixed(1)}/5`, cx, y + 90);
    y += ratingNumH + gap;

    // ---- DIVE BAR BADGE ----
    if (hasDiveBadge) {
      const badgeText = '🍺 Voted Dive Bar';
      ctx.font = '38px system-ui';
      const badgeWidth = ctx.measureText(badgeText).width + 60;
      const badgeX = (W - badgeWidth) / 2;
      ctx.strokeStyle = '#ffb700';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(badgeX, y, badgeWidth, 56, 28);
      ctx.stroke();
      ctx.fillStyle = '#ffb700';
      ctx.fillText(badgeText, cx, y + 38);
      y += diveBadgeH + gap;
    }

    // ---- SUB-RATINGS ----
    const subRatings = [
      { label: 'Price', value: rating.price, icon: '$' },
      { label: 'Vibe', value: rating.ambiance, icon: '🍸' },
      { label: 'Crowd', value: rating.crowd, icon: '👥' },
      { label: 'Svc', value: rating.service, icon: '⚡' },
      { label: 'Drinks', value: rating.drinkSelection, icon: '🍺' },
    ].filter(r => r.value != null && r.value > 0);

    if (subRatings.length > 0) {
      const boxW = 160;
      const boxH = 130;
      const boxGap = 18;
      const totalWidth = subRatings.length * boxW + (subRatings.length - 1) * boxGap;
      let boxX = (W - totalWidth) / 2;

      for (const sub of subRatings) {
        ctx.fillStyle = 'rgba(10, 10, 12, 0.8)';
        ctx.beginPath();
        ctx.roundRect(boxX, y, boxW, boxH, 14);
        ctx.fill();
        ctx.strokeStyle = 'rgba(80, 80, 90, 0.5)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(boxX, y, boxW, boxH, 14);
        ctx.stroke();

        const subCx = boxX + boxW / 2;
        ctx.fillStyle = '#999';
        ctx.font = '28px system-ui';
        ctx.fillText(sub.icon, subCx, y + 34);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 42px system-ui';
        ctx.fillText(parseFloat(sub.value).toFixed(1), subCx, y + 82);
        ctx.fillStyle = '#999';
        ctx.font = '24px system-ui';
        ctx.fillText(sub.label, subCx, y + 116);

        boxX += boxW + boxGap;
      }
      y += subRatingsH + gap;
    }

    // ---- DIVIDER + REVIEWER ----
    y += gap * 0.3;
    const dividerGrad = ctx.createLinearGradient(120, 0, W - 120, 0);
    dividerGrad.addColorStop(0, 'rgba(255, 45, 85, 0)');
    dividerGrad.addColorStop(0.5, 'rgba(255, 45, 85, 0.4)');
    dividerGrad.addColorStop(1, 'rgba(255, 45, 85, 0)');
    ctx.strokeStyle = dividerGrad;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(120, y);
    ctx.lineTo(W - 120, y);
    ctx.stroke();
    y += gap * 0.7;

    // ---- REVIEWER INFO ----
    const username = profile?.username || user?.email?.split('@')[0] || 'user';

    if (profile?.avatar_url) {
      try {
        const avatarImg = await loadImage(profile.avatar_url);
        const avatarSize = 72;
        ctx.font = '42px system-ui';
        const textW = ctx.measureText(`Reviewed by @${username}`).width;
        const totalLineW = avatarSize + 20 + textW;
        const avatarX = (W - totalLineW) / 2;
        ctx.save();
        ctx.beginPath();
        ctx.arc(avatarX + avatarSize / 2, y + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(avatarImg, avatarX, y, avatarSize, avatarSize);
        ctx.restore();
        ctx.fillStyle = '#ff2d55';
        ctx.font = '42px system-ui';
        ctx.textAlign = 'left';
        ctx.fillText(`Reviewed by @${username}`, avatarX + avatarSize + 20, y + avatarSize / 2 + 14);
        ctx.textAlign = 'center';
      } catch (e) {
        ctx.fillStyle = '#ff2d55';
        ctx.font = '42px system-ui';
        ctx.fillText(`Reviewed by @${username}`, cx, y + 40);
      }
    } else {
      ctx.fillStyle = '#ff2d55';
      ctx.font = '42px system-ui';
      ctx.fillText(`Reviewed by @${username}`, cx, y + 40);
    }

    // ---- FOOTER (anchored to bottom) ----
    const footerDivY = H - 200;

    const footerDividerGrad = ctx.createLinearGradient(100, 0, W - 100, 0);
    footerDividerGrad.addColorStop(0, 'rgba(255, 183, 0, 0)');
    footerDividerGrad.addColorStop(0.5, 'rgba(255, 183, 0, 0.3)');
    footerDividerGrad.addColorStop(1, 'rgba(255, 183, 0, 0)');
    ctx.strokeStyle = footerDividerGrad;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(100, footerDivY);
    ctx.lineTo(W - 100, footerDivY);
    ctx.stroke();

    ctx.fillStyle = '#ffb700';
    ctx.font = 'bold 44px system-ui';
    ctx.fillText('Dive Bar Ratings', cx, footerDivY + 60);

    ctx.fillStyle = '#888';
    ctx.font = '32px system-ui';
    ctx.fillText('Built for dive bars everywhere', cx, footerDivY + 108);

    ctx.fillStyle = '#555';
    ctx.font = '28px system-ui';
    ctx.fillText('gettilted.app', cx, footerDivY + 150);

    const fileName = `tilted-${bar.name.toLowerCase().replace(/\s+/g, '-')}-review.png`;

    return { canvas, fileName };
  };

  const handleNativeShare = async () => {
    setSharing(true);
    try {
      const { canvas, fileName } = await generateCardCanvas();

      if (Capacitor.isNativePlatform()) {
        // Native iOS: save image to cache then share with file
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        const { Filesystem, Directory } = await import('@capacitor/filesystem');
        const { Share } = await import('@capacitor/share');

        const base64Raw = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(blob);
        });

        const saved = await Filesystem.writeFile({
          path: fileName,
          data: base64Raw,
          directory: Directory.Cache
        });

        await Share.share({
          title: `My review of ${bar.name}`,
          text: `${bar.name} - ${overallScore.toFixed(1)}/5 on Tilted`,
          url: shareUrl,
          files: [saved.uri]
        });
        trackShare(bar.id, 'native');
      } else {
        // Web: try sharing with image file if supported
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        const file = new File([blob], fileName, { type: 'image/png' });

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `My review of ${bar.name}`,
            text: shareText,
            url: shareUrl,
            files: [file]
          });
          trackShare(bar.id, 'native');
        } else {
          // Fallback: URL-only share
          const shared = await nativeShare({
            title: `My review of ${bar.name}`,
            text: shareText,
            url: shareUrl,
          });
          if (shared) trackShare(bar.id, 'native');
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error sharing:', err);
      }
    } finally {
      setSharing(false);
    }
  };

  // Copy link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackShare(bar.id, 'copy_link');
    } catch (err) {
      console.error('Error copying link:', err);
    }
  };

  // Download/share image for Instagram
  const handleDownloadImage = async () => {
    setDownloading(true);
    try {
      const { canvas, fileName } = await generateCardCanvas();

      if (Capacitor.isNativePlatform()) {
        // Native iOS: share directly to Instagram Stories via UIPasteboard
        try {
          const base64 = canvas.toDataURL('image/png');
          console.log('[Instagram] Attempting native Stories share...');
          await shareImageToInstagramStories(base64);
          console.log('[Instagram] Native Stories share succeeded');
          trackShare(bar.id, 'instagram');
        } catch (err) {
          console.error('[Instagram] Native Stories share failed:', err.message);
          if (err.message?.includes('not installed')) {
            alert('Instagram is not installed on this device.');
          } else {
            alert(`Instagram share failed: ${err.message}`);
          }
        }
      } else {
        // Web/PWA: download image then open Instagram
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = fileName;
        link.href = blobUrl;
        link.click();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);

        // Open Instagram so user can upload the downloaded image
        setTimeout(() => {
          window.open('https://www.instagram.com/', '_blank');
        }, 500);

        trackShare(bar.id, 'instagram');
      }
    } catch (err) {
      console.error('Error generating image:', err);
    } finally {
      setDownloading(false);
    }
  };

  // Share to Facebook
  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
    trackShare(bar.id, 'facebook');
  };

  // Share to Twitter/X
  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
    trackShare(bar.id, 'twitter');
  };

  const shareAvailable = canShare();

  return (
    <div ref={modalRef} className="fixed inset-0 bg-black/80 z-50 overflow-y-auto overscroll-contain">
      {/* Close button always visible at top-right of screen */}
      <button
        onClick={onClose}
        className="fixed top-4 right-4 z-[60] bg-dive-dark/90 border border-dive-gray rounded-full p-2 text-gray-400 hover:text-white transition-colors"
        style={{ top: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
      >
        <X className="w-6 h-6" />
      </button>
      <div
        className="min-h-full flex items-start md:items-center justify-center p-4 pb-8"
        style={{ paddingTop: 'calc(4.5rem + env(safe-area-inset-top, 0px))' }}
      >
      <div className="bg-dive-dark border border-dive-gray rounded-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dive-gray">
          <h3 className="text-white font-medium flex items-center gap-2">
            <Share2 className="w-5 h-5 text-dive-neon" />
            Share Your Review
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preview Card */}
            <div ref={cardRef} className="p-4">
              <div className="bg-gradient-to-br from-dive-darker to-dive-dark border border-dive-neon/30 rounded-xl p-6 text-center">
                {/* Logo */}
                <img
                  src="/logo-full.png"
                  alt="Tilted"
                  className="h-14 mx-auto mb-4"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />

                <h4 className="text-white text-xl font-display mb-1">{bar.name}</h4>
                <p className="text-gray-400 text-sm mb-4 flex items-center justify-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {bar.neighborhood}
                </p>

                {/* Stars */}
                <div className="flex justify-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((star) => {
                    const filled = overallScore >= star;
                    const half = overallScore >= star - 0.5 && overallScore < star;

                    return (
                      <div key={star} className="relative w-8 h-8">
                        <svg
                          className="w-8 h-8 text-gray-600 absolute inset-0"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                        {filled && (
                          <svg
                            className="w-8 h-8 text-amber-400 absolute inset-0"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                        )}
                        {half && (
                          <div className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                            <svg
                              className="w-8 h-8 text-amber-400"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="text-amber-400 text-2xl font-bold mb-3">{overallScore.toFixed(1)}/5</div>

                {/* Dive Bar Badge */}
                {rating.isDiveBar && (
                  <div className="inline-flex items-center gap-1 px-3 py-1 border border-amber-400 text-amber-400 rounded-full text-xs mb-3">
                    🍺 Voted Dive Bar
                  </div>
                )}

                {/* Sub-ratings */}
                {(() => {
                  const subs = [
                    { label: 'Price', value: rating.price, icon: '$' },
                    { label: 'Vibe', value: rating.ambiance, icon: '🍸' },
                    { label: 'Crowd', value: rating.crowd, icon: '👥' },
                    { label: 'Svc', value: rating.service, icon: '⚡' },
                    { label: 'Drinks', value: rating.drinkSelection, icon: '🍺' },
                  ].filter(r => r.value != null && r.value > 0);
                  if (subs.length === 0) return null;
                  return (
                    <div className="flex justify-center gap-2 mb-3">
                      {subs.map((sub) => (
                        <div
                          key={sub.label}
                          className="bg-dive-dark/80 border border-dive-gray/50 rounded-lg px-2.5 py-1.5 text-center min-w-[48px]"
                        >
                          <div className="text-[10px] text-gray-500 mb-0.5">{sub.icon}</div>
                          <div className="text-white text-xs font-bold">{parseFloat(sub.value).toFixed(1)}</div>
                          <div className="text-gray-500 text-[9px]">{sub.label}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Reviewer Info */}
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  {profile?.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt=""
                      className="w-5 h-5 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-dive-neon/20 flex items-center justify-center">
                      <span className="text-dive-neon text-[10px] font-medium">
                        {(profile?.display_name || profile?.username || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <span className="text-dive-neon text-sm">
                    Reviewed by @{profile?.username || user?.email?.split('@')[0] || 'user'}
                  </span>
                </div>

                {/* Footer branding */}
                <div className="border-t border-dive-gray/50 pt-3 mt-3">
                  <p className="text-amber-400/80 text-xs font-medium">Dive Bar Ratings</p>
                  <p className="text-gray-600 text-[10px] mt-0.5">Built for dive bars everywhere</p>
                </div>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="p-4 border-t border-dive-gray space-y-3">
              {/* Native Share (mobile) */}
              {shareAvailable && (
                <button
                  onClick={handleNativeShare}
                  disabled={sharing}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-dive-neon text-white rounded-lg hover:bg-dive-neon/90 transition-colors disabled:opacity-70"
                >
                  {sharing ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Share2 className="w-5 h-5" />
                  )}
                  {sharing ? 'Sharing...' : 'Share'}
                </button>
              )}

              {/* Download for Instagram */}
              <button
                onClick={handleDownloadImage}
                disabled={downloading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                {downloading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Instagram className="w-5 h-5" />
                )}
                {downloading ? 'Creating...' : 'Post to Instagram Story'}
              </button>

              {/* Social Buttons Grid */}
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={shareToFacebook}
                  className="flex flex-col items-center justify-center gap-1 px-4 py-3 bg-[#1877F2] text-white rounded-lg hover:opacity-90 transition-opacity"
                >
                  <Facebook className="w-5 h-5" />
                  <span className="text-xs">Facebook</span>
                </button>

                <button
                  onClick={shareToTwitter}
                  className="flex flex-col items-center justify-center gap-1 px-4 py-3 bg-black text-white rounded-lg border border-gray-700 hover:bg-gray-900 transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                  <span className="text-xs">X</span>
                </button>

                <button
                  onClick={handleCopyLink}
                  className="flex flex-col items-center justify-center gap-1 px-4 py-3 bg-dive-gray text-white rounded-lg hover:bg-dive-light transition-colors"
                >
                  {copied ? <Check className="w-5 h-5 text-green-400" /> : <Link2 className="w-5 h-5" />}
                  <span className="text-xs">{copied ? 'Copied!' : 'Copy Link'}</span>
                </button>
              </div>
            </div>
      </div>
      </div>
    </div>
  );
}

export default ShareReviewModal;
